import {Component, PanelLifecycleContext} from 'panel';
import type {
  CopilotAgentIntelligenceCard,
  CopilotAgentIntelligenceContext,
  CopilotCardContext,
  CopilotCurrentPageContext,
  CopilotHomeCard,
  CopilotHomeContext,
  CopilotProfileContext,
  CopilotRcaBoardContext,
} from '@mixpanel/ai-copilot/agent/types';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';

import {Subscribable} from 'common/types/context/subscribable';
import {BaseQueryErrorType, BaseQueryResultsType} from 'common/report/types';
import {Annotation} from 'common/report/model-resources/annotations/types';
import {BookmarkParamsUnionType} from 'common/report/dashboards/types';
import {ChartType, InsightsBookmarkParams} from 'common/types/reports/bookmark';
import type {ReportBookmarkManager} from 'common/report/insights/contexts/report-bookmark-manager';
import type {FilterEntry, SortOption} from 'common/widgets/recording-player/types';
import type {DateRangeAttributes} from 'common/widgets/date-range-picker/models/types';
import type {Playlist} from 'common/widgets/playlists/types';

/** The Insights bookmark fields sent to the agent as `reportContext.request`; the rest stay client-side. */
export const LLM_INSIGHTS_REQUEST_FIELDS = [
  `displayOptions`,
  `liftComparison`,
  `name`,
  `sections`,
  `timeComparison`,
] as const satisfies ReadonlyArray<keyof InsightsBookmarkParams>;

export interface SessionReplayFiltersUpdate {
  eventFilters?: FilterEntry[];
  dateRange?: DateRangeAttributes;
  sortOption?: SortOption;
}

export interface PlaylistCardData {
  name: string;
  filters: Playlist[`filters`];
  // What the card has loaded, which for a large playlist is a prefix of the whole.
  replayIds: string[];
  totalReplayCount: number;
  truncated: boolean;
}

export interface ReplayPlaybackRequest {
  replayId: string;
  eventTime: number; // absolute unix seconds
}

export type CopilotBoardSettings = Pick<
  InternalCopilotBoardContext,
  `dateRange` | `filters` | `groupClauses` | `cohortClauses`
>;

export type AgentIntelligencePageInput =
  | ({pageType: `usage`; cards: CopilotAgentIntelligenceCard[]} & CopilotBoardSettings)
  | Exclude<CopilotAgentIntelligenceContext, {pageType: `usage`}>;

export interface ReplayDisplayMetadata {
  time: number; // unix seconds (replay start)
  distinctId: string;
  name: string;
  email: string;
}

export type BoardEditedAction = `add` | `remove` | `update` | `updateBoard`;

export interface BoardEditedDetail {
  dashboardId: number;
  bookmarkId?: number;
  contentId?: string | number;
  action: BoardEditedAction;
  toolCallId: string;
}

export interface BoardSaveDetail {
  dashboardId: number;
  toolCallId: string;
}

export interface EventMetadataFilledDetail {
  toolCallId: string;
}

// `_rawRequest` holds the untransformed bookmark.params reference for cheap
// change detection; it's stripped before any payload leaves the FE.
// `playlist` holds a playlist card's loaded replay data; serialized into
// boardContext.playlists by generateContextBody.
type InternalCopilotCardContext = CopilotCardContext & {
  _rawRequest?: BookmarkParamsUnionType;
  playlist?: {name: string; replayIds: string[]; totalReplayCount: number; truncated: boolean};
};

type InternalCopilotBoardContext = {
  dashboardId?: string | number;
  title?: string;
  description?: string;
  canEdit?: boolean;
  cards: Record<string, InternalCopilotCardContext>;
  // Board-level applied date range (bookmark time section), when the user has an
  // active board date filter. Overrides each card's saved range on the agent side.
  dateRange?: InsightsBookmarkParams[`sections`][`time`];
  // Board-level applied filters (bookmark filter clauses), when the user has
  // active board filters. Appended to each card's saved filters on the agent side.
  filters?: InsightsBookmarkParams[`sections`][`filter`];
  // Board-level applied breakdowns, split into property `group` clauses and
  // `cohorts` clauses (matching `sections.group` / `sections.cohorts`). Appended
  // to each card's saved breakdowns on the agent side as innermost breakdowns.
  groupClauses?: InsightsBookmarkParams[`sections`][`group`];
  cohortClauses?: InsightsBookmarkParams[`sections`][`cohorts`];
};

/**
 * Which product page (and lifecycle phase) the Spark sidebar is currently looking at.
 * This is a presentation-layer contract read by the suggested-prompts component to pick
 * a context-appropriate prompt set. It is derived by the host pages (see the *-suggestions
 * files under empty-state/) and is intentionally separate from the per-surface LLM context
 * payloads above — those describe the entity, this describes the page.
 *
 * Note: setup vs results is a *tab* distinction on the experiment page (Configuration vs
 * Overview/Diagnostics), not a route distinction, so only the experiment page can compute it.
 */
export enum CopilotPageType {
  EXPERIMENT_LIST = `experiment-list`,
  EXPERIMENT_SETUP = `experiment-setup`,
  EXPERIMENT_RESULTS = `experiment-results`,
  FLAG_LIST = `flag-list`,
  FLAG_DETAIL = `flag-detail`,
  OTHER = `other`,
}

export interface CopilotPageDescriptor {
  pageType: CopilotPageType;
  // The experiment_id / flag_id in focus, when the page is scoped to a single entity.
  entityId?: string;
  // The entity's lifecycle state (e.g. draft | configured | active | concluded | success for
  // experiments; draft | enabled | disabled for flags). A loose string so richer FE-derived
  // statuses pass through unchanged.
  entityState?: string;
  // Whether the in-focus experiment has a primary metric chosen; gates the setup page's
  // "Check my sample size" prompt. On the descriptor (not the surface context) so it refreshes
  // reactively — setExperimentContext doesn't notify on same-id metric changes.
  hasPrimaryMetric?: boolean;
}

// Shallow comparison over the descriptor's four primitive fields. An explicit compare is clearer
// than a generic key-union loop for this small, fixed shape and correctly treats a missing optional
// field the same as an explicit `undefined` (both read as `undefined`).
function pageDescriptorsEqual(a: CopilotPageDescriptor, b: CopilotPageDescriptor): boolean {
  return (
    a.pageType === b.pageType &&
    a.entityId === b.entityId &&
    a.entityState === b.entityState &&
    a.hasPrimaryMetric === b.hasPrimaryMetric
  );
}

export class CopilotCurrentPageManager implements PanelLifecycleContext, Subscribable {
  private connectedComponents: Set<Component<any>> = new Set();
  private subscriptions: Array<() => void> = [];

  private reportRequest: {params?: BookmarkParamsUnionType} | null = null;
  private reportResponse: BaseQueryResultsType | BaseQueryErrorType | null = null;
  private reportTitle: string | null = null;
  private reportDescription: string | null = null;
  private reportBookmarkId: number | null = null;
  // URL-fragment slug for the active report. Set by the report-editor when
  // it computes/regenerates one (typically for unsaved/ad-hoc reports —
  // saved reports fall back to bookmarkId). LLM-side fallback for tools
  // that accept either id or slug.
  private reportBookmarkUrlSlug: string | null = null;
  private annotations: Annotation[] = [];
  private annotationsHash: string = ``;

  boardContext: InternalCopilotBoardContext | null = null;
  // The user's pinned cards on the home page, serialized for the LLM. Unlike
  // boardContext, there is no backing dashboard to fetch on demand, so the cards
  // are held (and later sent) inline. Home shows at most a few pinned cards.
  private homeContext: CopilotHomeContext | null = null;
  private agentIntelligenceContext: CopilotAgentIntelligenceContext | null = null;
  // RCA run metadata for the currently-viewed AI-RCA board. Stored separately
  // from boardContext because it arrives asynchronously (fetchRcaBoardContext)
  // and must survive boardContext re-initialization on editor exit; matched to
  // the active board by dashboardId at payload time.
  private rcaCtx: CopilotRcaBoardContext | null = null;
  // The active report's bookmark manager. Apply-edit tools (e.g. `editMetric`) read
  // current params off this and call `applyParams` to push their changes back through
  // the report editor.
  public reportBookmark: ReportBookmarkManager | null = null;
  private profileDistinctId: string | null = null;
  private profileDataGroupId: string | null = null;
  private profileProperties: Record<string, unknown> | null = null;
  // The profile's event activity stream, pushed by the ProfileActivity widget
  // (see setProfileActivityStream). Keyed by the distinctId it belongs to so a
  // late push for a since-navigated-away profile can't surface under the wrong one.
  private profileEvents: UnknownObj[] | null = null;
  private profileEventsDistinctId: string | null = null;
  // Query metadata for the activity stream, so the agent can interpret it: the
  // date-range window it was queried over and any event-name filters applied.
  private profileActivityDateRange: UnknownObj | null = null;
  private profileIncludedEvents: string[] | null = null;
  private profileExcludedEvents: string[] | null = null;
  private experimentCtx: CopilotCurrentPageContext[`experimentContext`] | null = null;
  private featureFlagCtx: CopilotCurrentPageContext[`featureFlagContext`] | null = null;
  private agentFlowCtx: CopilotCurrentPageContext[`agentFlowContext`] | null = null;
  private eventsCtx: CopilotCurrentPageContext[`eventsContext`] | null = null;
  private usersCtx: CopilotCurrentPageContext[`usersContext`] | null = null;

  // Which page the sidebar is on. Drives the suggested-prompt set. Defaults to
  // `other` so prompts fall back to the generic defaults until a host page claims the page.
  private pageDescriptor: CopilotPageDescriptor = {pageType: CopilotPageType.OTHER};
  // Identity of the host page that last published the descriptor, so a stale unmount cleanup
  // can't clobber a descriptor that a newer page has already published (see clearPageDescriptor).
  private pageDescriptorOwner: UnknownObj | null = null;

  // Session Replay handler — enables Copilot to update filters, date range, and sort on the SR page
  private sessionReplayHandler: ((update: SessionReplayFiltersUpdate) => void) | null = null;
  private pendingSessionReplayFiltersUpdate: SessionReplayFiltersUpdate | null = null;
  private sessionReplayCtx: CopilotCurrentPageContext[`sessionReplayContext`] | null = null;

  // Replay playback handler — enables Copilot to jump the SR page's player to a specific moment
  private replayPlaybackHandler: ((req: ReplayPlaybackRequest) => void) | null = null;

  // Client-only per-replay display metadata used by copilot tool render components.
  private replayDisplayMetadata: Map<string, ReplayDisplayMetadata> = new Map();

  // Board edited handler — enables the dashboards page to refresh after Copilot mutates a board.
  // The handler can be async; rejections are caught in notifyBoardEdited so a transient fetch
  // failure does not become an unhandled promise rejection.
  private boardEditedHandler: ((detail: BoardEditedDetail) => void | Promise<void>) | null = null;
  // Dedupes notifications by toolCallId so re-mounts of the tool result component don't re-fire
  private notifiedBoardEditedToolCallIds: Set<string> = new Set();

  // Board saving / saved handlers — let the dashboards page reflect saveBoardTool progress
  // (in-flight indicator while the agent finalizes a draft) and refresh once it succeeds.
  // Same async + dedupe semantics as boardEditedHandler.
  private boardSavingHandler: ((detail: BoardSaveDetail) => void | Promise<void>) | null = null;
  private boardSavedHandler: ((detail: BoardSaveDetail) => void | Promise<void>) | null = null;
  private notifiedBoardSavingToolCallIds: Set<string> = new Set();
  private notifiedBoardSavedToolCallIds: Set<string> = new Set();

  // Lets the Lexicon events page refetch its table once the fillEventMetadata tool finishes, so the
  // generated names/descriptions appear without a manual reload.
  // Same async + dedupe semantics as boardEditedHandler.
  private eventMetadataFilledHandler: ((detail: EventMetadataFilledDetail) => void | Promise<void>) | null = null;
  private notifiedEventMetadataFilledToolCallIds: Set<string> = new Set();

  setReportRequest(request: {
    params?: BookmarkParamsUnionType;
    name?: string;
    description?: string;
    bookmarkId?: number;
    bookmarkUrlSlug?: string;
  }) {
    // Only notify if the params reference has actually changed
    if (
      this.reportRequest?.params === request?.params &&
      this.reportTitle === (request?.name ?? null) &&
      this.reportDescription === (request?.description ?? null) &&
      this.reportBookmarkId === (request?.bookmarkId ?? null) &&
      this.reportBookmarkUrlSlug === (request?.bookmarkUrlSlug ?? null)
    ) {
      return;
    }
    this.reportRequest = request;
    this.reportTitle = request?.name ?? null;
    this.reportDescription = request?.description ?? null;
    this.reportBookmarkId = request?.bookmarkId ?? null;
    this.reportBookmarkUrlSlug = request?.bookmarkUrlSlug ?? null;
    this.updateComponents();
  }

  // Stored separately from `setReportRequest` because `reportRequest` is the
  // serialized snapshot copilot sends to the LLM, while `reportBookmark` is a live
  // manager handle the FE uses to apply edits back to the page. Keeping them split
  // avoids coupling the LLM-context payload to FE-only state.
  setReportBookmark(bookmarkManager: ReportBookmarkManager | null) {
    if (this.reportBookmark === bookmarkManager) {
      return;
    }
    this.reportBookmark = bookmarkManager;
    this.updateComponents();
  }

  setReportResponse(response: BaseQueryResultsType | BaseQueryErrorType) {
    // Only notify if the response reference has actually changed
    if (this.reportResponse === response) {
      return;
    }
    this.reportResponse = response;
    this.updateComponents();
  }

  setAnnotations(annotations: Annotation[]) {
    // Create a simple hash from annotations to detect content changes
    // Why: the annotations array is a new reference every time due to getAllValues
    // and doing it this way is simpler than a deep comparison
    const newHash = `${annotations.length}:${annotations
      .sort((a) => a.id)
      .map((a) => a.id)
      .join(`,`)}`;

    // Only notify if the annotations content has actually changed
    if (this.annotationsHash === newHash) {
      return;
    }

    this.annotations = annotations;
    this.annotationsHash = newHash;
    this.updateComponents();
  }

  clearReportContext() {
    this.reportRequest = null;
    this.reportResponse = null;
    this.reportTitle = null;
    this.reportDescription = null;
    this.reportBookmarkId = null;
    this.reportBookmarkUrlSlug = null;
    this.annotations = [];
    this.reportBookmark = null;

    // Do not updateComponents during clear methods as they are triggered on component unmounts
  }

  hasReportContext(): boolean {
    return !!(this.reportRequest && this.reportResponse);
  }

  hasReportRequestContext(): boolean {
    return !!this.reportRequest;
  }

  // The active report's chart type (e.g. "line", "column", "funnel-steps"),
  // read off the live bookmark params. Used to gate chart-type-specific Copilot
  // affordances such as the RCA suggestion. Undefined when no report is active
  // or the params carry no displayOptions.
  getReportChartType(): ChartType | undefined {
    const params = this.reportRequest?.params;
    if (!params) {
      return undefined;
    }
    return (params as {displayOptions?: {chartType?: ChartType}}).displayOptions?.chartType;
  }

  getReportContextForLlm(): CopilotCurrentPageContext[`reportContext`] {
    return {
      title: this.reportTitle ?? undefined,
      description: this.reportDescription ?? undefined,
      bookmarkId: this.reportBookmarkId ?? undefined,
      bookmarkUrlSlug: this.reportBookmarkUrlSlug ?? undefined,
      request: this.toLlmReportRequestContext(this.reportRequest?.params) || {},
      response: this.toLlmReportResponseContext(this.reportResponse || undefined) || {},
      annotations: this.annotations.filter(
        (annotation) => !(annotation.tags?.length === 1 && annotation.tags?.at(0)?.name === `Mixpanel System Event`),
      ),
    };
  }

  getReportRequestContextForLlm() {
    return {
      request: this.toLlmReportRequestContext(this.reportRequest?.params) || {},
    };
  }

  initializeBoardContext(dashboardId: number, title?: string, description?: string, canEdit?: boolean) {
    this.boardContext = {
      dashboardId,
      title,
      description,
      canEdit,
      cards: {},
    };
    this.updateComponents();
  }

  // Syncs the board's currently-applied date range (from the board date picker /
  // URL) so the agent can honor it — it overrides each card's saved range. Runs on
  // every board update, so it change-detects to avoid an update->render->update loop.
  setBoardDateRange(dateRange: InsightsBookmarkParams[`sections`][`time`] | undefined) {
    if (!this.boardContext) {
      return;
    }
    const next = dateRange?.length ? dateRange : undefined;
    if (JSON.stringify(this.boardContext.dateRange) === JSON.stringify(next)) {
      return;
    }
    this.boardContext.dateRange = next;
    this.updateComponents();
  }

  // Syncs the board's currently-applied filters (from the board's filter bar /
  // URL) so the agent can honor them — they apply on top of each card's saved
  // filters. Runs on every board update, so it change-detects to avoid an
  // update->render->update loop.
  setBoardFilters(filters: InsightsBookmarkParams[`sections`][`filter`] | undefined) {
    if (!this.boardContext) {
      return;
    }
    const next = filters?.length ? filters : undefined;
    if (isEqual(this.boardContext.filters, next)) {
      return;
    }
    this.boardContext.filters = next;
    this.updateComponents();
  }

  // Syncs the board's currently-applied breakdowns (from the board's breakdown
  // bar / URL) so the agent can honor them — they are appended as innermost
  // breakdowns to each card's saved breakdowns. Split into property `group`
  // clauses and `cohorts` clauses. Runs on every board update, so it
  // change-detects to avoid an update->render->update loop.
  setBoardBreakdowns(
    breakdowns:
      | {
          group?: InsightsBookmarkParams[`sections`][`group`];
          cohorts?: InsightsBookmarkParams[`sections`][`cohorts`];
        }
      | undefined,
  ) {
    if (!this.boardContext) {
      return;
    }
    const nextGroup = breakdowns?.group?.length ? breakdowns.group : undefined;
    const nextCohorts = breakdowns?.cohorts?.length ? breakdowns.cohorts : undefined;
    if (isEqual(this.boardContext.groupClauses, nextGroup) && isEqual(this.boardContext.cohortClauses, nextCohorts)) {
      return;
    }
    this.boardContext.groupClauses = nextGroup;
    this.boardContext.cohortClauses = nextCohorts;
    this.updateComponents();
  }

  setBoardCardContext(
    cardId: string,
    dashboardId: number,
    metadata: {
      bookmarkId?: number;
      contentType?: string;
      title?: string;
      description?: string;
      textContent?: string;
      request?: BookmarkParamsUnionType;
    },
  ) {
    if (!this.boardContext) {
      return;
    }

    // Guard against race condition: ignore updates from old board
    if (this.boardContext.dashboardId !== dashboardId) {
      return;
    }

    const existing = this.boardContext.cards[cardId];
    // Reference-compare the raw params object; dashboards re-emit the same
    // bookmark.params reference until the bookmark itself reloads, so a stable
    // ref means nothing changed.
    if (
      existing &&
      existing.bookmarkId === metadata.bookmarkId &&
      existing.contentType === metadata.contentType &&
      existing.title === metadata.title &&
      existing.description === metadata.description &&
      existing.textContent === metadata.textContent &&
      existing._rawRequest === metadata.request
    ) {
      return;
    }

    this.boardContext.cards[cardId] = {
      bookmarkId: metadata.bookmarkId,
      contentType: metadata.contentType,
      title: metadata.title,
      description: metadata.description,
      textContent: metadata.textContent,
      request: this.toLlmReportRequestContext(metadata.request),
      _rawRequest: metadata.request,
    };

    this.updateComponents();
  }

  // Stores a playlist card's loaded replay list alongside its metadata; unlike
  // report cards, that list is resolved client-side and so is sent inline.
  setBoardPlaylistCardContext(
    cardId: string,
    dashboardId: number,
    playlistData: PlaylistCardData,
    metadata?: {title?: string; description?: string},
  ) {
    if (!this.boardContext) {
      return;
    }

    if (this.boardContext.dashboardId !== dashboardId) {
      return;
    }

    const playlist = {
      name: playlistData.name,
      replayIds: playlistData.replayIds,
      totalReplayCount: playlistData.totalReplayCount,
      truncated: playlistData.truncated,
    };

    // The card re-emits its recordings on every render with freshly built arrays,
    // so notifying unconditionally would loop through updateComponents.
    const existing = this.boardContext.cards[cardId];
    if (
      existing &&
      existing.title === metadata?.title &&
      existing.description === metadata?.description &&
      isEqual(existing.playlist, playlist)
    ) {
      return;
    }

    this.boardContext.cards[cardId] = {
      contentType: `playlist`,
      title: metadata?.title,
      description: metadata?.description,
      playlist,
    };

    this.updateComponents();
  }

  // Drops context for cards that are no longer on the board. Without it a deleted
  // card's data — a playlist's replay list in particular — keeps reaching the agent
  // until the board is re-initialized.
  pruneBoardCardContext(liveCardIds: string[]) {
    if (!this.boardContext) {
      return;
    }

    const live = new Set(liveCardIds);
    const stale = Object.keys(this.boardContext.cards).filter((cardId) => !live.has(cardId));
    if (!stale.length) {
      return;
    }

    for (const cardId of stale) {
      delete this.boardContext.cards[cardId];
    }
    this.updateComponents();
  }

  // Push RCA run metadata into the page context for AI-RCA generated boards.
  // Change-detected because both the board page's componentDidUpdate and the
  // async run fetch call this, often with identical data.
  setRcaContext(ctx: CopilotRcaBoardContext) {
    if (JSON.stringify(this.rcaCtx) === JSON.stringify(ctx)) {
      return;
    }
    this.rcaCtx = ctx;
    this.updateComponents();
  }

  clearBoardContext() {
    this.boardContext = null;
    this.rcaCtx = null;
    // Reset the per-board dedupe sets so a future board's edits/saves aren't
    // blocked by stale toolCallIds, and to keep the sets bounded across long
    // sessions.
    this.notifiedBoardEditedToolCallIds.clear();
    this.notifiedBoardSavingToolCallIds.clear();
    this.notifiedBoardSavedToolCallIds.clear();

    // Do not updateComponents during clear methods as they are triggered on component unmounts
  }

  // Serializes the home page's pinned cards for the LLM. `request` carries the
  // bookmark's semantic params (cleaned of display-only fields, same as reports)
  // so the agent can understand a card's config without a round-trip; it can
  // still fetch results via `queryBookmark` using the card's bookmarkId.
  setHomeContext(cards: CopilotHomeCard[]) {
    const next: CopilotHomeContext = {
      cards: cards.map((card) => ({
        bookmarkId: card.bookmarkId,
        contentType: card.contentType,
        title: card.title,
        description: card.description,
        request: this.toLlmReportRequestContext(card.request),
      })),
    };
    // Pinned cards reformat to fresh object references on every home render, so
    // compare by value to avoid a re-render loop through updateComponents().
    if (isEqual(this.homeContext, next)) {
      return;
    }
    this.homeContext = next;
    this.updateComponents();
  }

  clearHomeContext() {
    this.homeContext = null;
    // Do not updateComponents during clear methods as they are triggered on component unmounts
  }

  hasHomeContext(): boolean {
    return !!this.homeContext?.cards.length;
  }

  getHomeContextForLlm(): CopilotHomeContext | undefined {
    return this.hasHomeContext() ? this.homeContext! : undefined;
  }

  setAgentIntelligenceContext(page: AgentIntelligencePageInput) {
    const next: CopilotAgentIntelligenceContext =
      page.pageType === `usage`
        ? {
            ...page,
            // Widened to the wire type as for the board's settings: the payload is plain JSON.
            filters: page.filters as unknown as UnknownObj[] | undefined,
            groupClauses: page.groupClauses as unknown as UnknownObj[] | undefined,
            cohortClauses: page.cohortClauses as unknown as UnknownObj[] | undefined,
          }
        : page;
    // This builds a new object on every call, so compare by value to avoid a
    // re-render loop through updateComponents().
    if (isEqual(this.agentIntelligenceContext, next)) {
      return;
    }
    this.agentIntelligenceContext = next;
    this.updateComponents();
  }

  clearAgentIntelligenceContext() {
    this.agentIntelligenceContext = null;
    // Do not updateComponents during clear methods as they are triggered on component unmounts
  }

  hasAgentIntelligenceContext(): boolean {
    return this.agentIntelligenceContext !== null;
  }

  getAgentIntelligenceContextForLlm(): CopilotAgentIntelligenceContext | undefined {
    return this.agentIntelligenceContext ?? undefined;
  }

  setProfileContext(distinctId: string, details?: {dataGroupId?: string; properties?: Record<string, unknown>}) {
    const isNewProfile = this.profileDistinctId !== distinctId;
    if (isNewProfile) {
      this.clearBoardContext();
      this.clearReportContext();
      this.clearExperimentContext();
      this.clearFeatureFlagContext();
      this.clearAgentFlowContext();
      this.clearEventsContext();
      this.clearUsersContext();
      // Drop a prior profile's activity stream — unless the ProfileActivity widget
      // already pushed this profile's events ahead of the page setting the id.
      if (this.profileEventsDistinctId !== distinctId) {
        this.profileEvents = null;
        this.profileEventsDistinctId = null;
        this.profileActivityDateRange = null;
        this.profileIncludedEvents = null;
        this.profileExcludedEvents = null;
      }
    }

    // Always store the latest details so the LLM gets fresh data as the profile's
    // properties load in asynchronously after the distinctId is known.
    // Only fire updateComponents on a new profile to avoid Proxy re-render loops.
    this.profileDistinctId = distinctId;
    this.profileDataGroupId = details?.dataGroupId ?? null;
    this.profileProperties = details?.properties ?? null;
    if (isNewProfile) {
      this.updateComponents();
    }
  }

  // Pushed by the ProfileActivity widget (full-page profile only, not the sidebar)
  // once the user's event activity stream loads. Stored separately from the
  // page-set distinctId/properties and keyed by distinctId so it only surfaces
  // for the profile currently in view. liveEvents can't fetch a user's events by
  // distinctId, so this is the only way the agent sees the activity stream.
  setProfileActivityStream(
    distinctId: string,
    events: Obj[],
    details?: {dateRange?: UnknownObj; includedEvents?: string[]; excludedEvents?: string[]},
  ) {
    this.profileEvents = events;
    this.profileEventsDistinctId = distinctId;
    this.profileActivityDateRange = details?.dateRange ?? null;
    this.profileIncludedEvents = details?.includedEvents ?? null;
    this.profileExcludedEvents = details?.excludedEvents ?? null;
  }

  clearProfileActivityStream() {
    this.profileEvents = null;
    this.profileEventsDistinctId = null;
    this.profileActivityDateRange = null;
    this.profileIncludedEvents = null;
    this.profileExcludedEvents = null;
  }

  clearProfileContext() {
    if (!this.profileDistinctId) {
      return;
    }
    this.profileDistinctId = null;
    this.profileDataGroupId = null;
    this.profileProperties = null;
    this.profileEvents = null;
    this.profileEventsDistinctId = null;
    this.profileActivityDateRange = null;
    this.profileIncludedEvents = null;
    this.profileExcludedEvents = null;
  }

  hasProfileContext(): boolean {
    return !!this.profileDistinctId;
  }

  getProfileContextForLlm(): CopilotProfileContext | undefined {
    if (!this.hasProfileContext()) {
      return undefined;
    }
    // Only include the activity stream (and its query metadata) when it belongs
    // to the profile in view.
    const activityMatches = !!this.profileEvents && this.profileEventsDistinctId === this.profileDistinctId;
    return {
      distinctId: this.profileDistinctId!,
      dataGroupId: this.profileDataGroupId ?? undefined,
      properties: this.profileProperties ?? undefined,
      events: activityMatches ? this.profileEvents! : undefined,
      activityDateRange: activityMatches ? (this.profileActivityDateRange ?? undefined) : undefined,
      includedEvents: activityMatches && this.profileIncludedEvents?.length ? this.profileIncludedEvents : undefined,
      excludedEvents: activityMatches && this.profileExcludedEvents?.length ? this.profileExcludedEvents : undefined,
    };
  }

  setExperimentContext(ctx: CopilotCurrentPageContext[`experimentContext`]) {
    const isNewExperiment = this.experimentCtx?.id !== ctx?.id;
    if (isNewExperiment) {
      this.clearReportContext();
      this.clearBoardContext();
      this.clearProfileContext();
      this.clearFeatureFlagContext();
      this.clearAgentFlowContext();
      this.clearEventsContext();
      this.clearUsersContext();
    }

    // Always update the stored context so the LLM gets fresh data (e.g. when results load).
    // Only fire updateComponents on new experiment to avoid Proxy re-render loops.
    this.experimentCtx = ctx;
    if (isNewExperiment) {
      this.updateComponents();
    }
  }

  clearExperimentContext() {
    this.experimentCtx = null;
  }

  hasExperimentContext(): boolean {
    return !!this.experimentCtx;
  }

  getExperimentContextForLlm(): CopilotCurrentPageContext[`experimentContext`] | undefined {
    return this.experimentCtx ?? undefined;
  }

  setFeatureFlagContext(ctx: CopilotCurrentPageContext[`featureFlagContext`]) {
    const isNewFlag = this.featureFlagCtx?.id !== ctx?.id;
    if (isNewFlag) {
      this.clearReportContext();
      this.clearBoardContext();
      this.clearProfileContext();
      this.clearExperimentContext();
      this.clearAgentFlowContext();
      this.clearEventsContext();
      this.clearUsersContext();
    }

    // Always update stored context so the LLM gets fresh data on setting changes.
    // Only fire updateComponents on new flag to avoid Proxy re-render loops.
    this.featureFlagCtx = ctx;
    if (isNewFlag) {
      this.updateComponents();
    }
  }

  clearFeatureFlagContext() {
    this.featureFlagCtx = null;
  }

  hasFeatureFlagContext(): boolean {
    return !!this.featureFlagCtx;
  }

  getFeatureFlagContextForLlm(): CopilotCurrentPageContext[`featureFlagContext`] | undefined {
    return this.featureFlagCtx ?? undefined;
  }

  setAgentFlowContext(ctx: CopilotCurrentPageContext[`agentFlowContext`]) {
    const isNewAgentFlow = this.agentFlowCtx?.id !== ctx?.id;
    if (isNewAgentFlow) {
      this.clearReportContext();
      this.clearBoardContext();
      this.clearProfileContext();
      this.clearExperimentContext();
      this.clearFeatureFlagContext();
      this.clearEventsContext();
      this.clearUsersContext();
    }

    // Always update stored context so the LLM gets fresh data on status/config changes.
    // Only fire updateComponents on new Automation to avoid Proxy re-render loops.
    this.agentFlowCtx = ctx;
    if (isNewAgentFlow) {
      this.updateComponents();
    }
  }

  clearAgentFlowContext() {
    this.agentFlowCtx = null;
  }

  hasAgentFlowContext(): boolean {
    return !!this.agentFlowCtx;
  }

  getAgentFlowContextForLlm(): CopilotCurrentPageContext[`agentFlowContext`] | undefined {
    return this.agentFlowCtx ?? undefined;
  }

  // Page descriptor for suggested prompts. Unlike the per-surface LLM contexts, this
  // is FE-only state read by the empty-state to select prompts; it is not sent to the agent
  // (the entity id/state the prompts need already rides along in the surface context above).
  // The optional `owner` is an opaque identity token (see useCopilotPageDescriptor) so the manager
  // can tell which page currently owns the descriptor and ignore stale cleanups from a page that
  // has since been superseded.
  setPageDescriptor(descriptor: CopilotPageDescriptor, owner?: UnknownObj) {
    this.pageDescriptorOwner = owner ?? null;
    if (pageDescriptorsEqual(this.pageDescriptor, descriptor)) {
      return;
    }
    this.pageDescriptor = descriptor;
    this.updateComponents();
  }

  getPageDescriptor(): CopilotPageDescriptor {
    return this.pageDescriptor;
  }

  // Called by host pages on unmount to revert the sidebar to generic prompts. Unlike the
  // other clear* methods (which stay silent because their context is re-read lazily on the
  // next send), this must notify: the empty-state selects prompts synchronously off the
  // descriptor, so without a notify the sidebar would keep showing the old page's prompts
  // after navigating away.
  //
  // When an `owner` is supplied, the clear is ignored if a newer page has already published its
  // descriptor — this prevents a late/replayed unmount cleanup from resetting the sidebar to
  // generic prompts while a different page is still mounted.
  clearPageDescriptor(owner?: UnknownObj) {
    if (owner !== undefined && this.pageDescriptorOwner !== owner) {
      return;
    }
    this.pageDescriptorOwner = null;
    if (this.pageDescriptor.pageType === CopilotPageType.OTHER && this.pageDescriptor.entityId === undefined) {
      return;
    }
    this.pageDescriptor = {pageType: CopilotPageType.OTHER};
    this.updateComponents();
  }

  setEventsContext(ctx: CopilotCurrentPageContext[`eventsContext`]) {
    // Clear siblings on first entry to the Events page; subsequent syncs (new
    // query results) just refresh the stored snapshot. Notify on the first entry
    // only to avoid Proxy re-render loops, matching the feature-flag pattern.
    const isNewEntry = !this.eventsCtx;
    if (isNewEntry) {
      this.clearReportContext();
      this.clearBoardContext();
      this.clearProfileContext();
      this.clearExperimentContext();
      this.clearFeatureFlagContext();
      this.clearAgentFlowContext();
      this.clearUsersContext();
    }

    this.eventsCtx = ctx;
    if (isNewEntry) {
      this.updateComponents();
    }
  }

  clearEventsContext() {
    this.eventsCtx = null;
  }

  hasEventsContext(): boolean {
    return !!this.eventsCtx;
  }

  getEventsContextForLlm(): CopilotCurrentPageContext[`eventsContext`] | undefined {
    return this.eventsCtx ?? undefined;
  }

  setUsersContext(ctx: CopilotCurrentPageContext[`usersContext`]) {
    const isNewEntry = !this.usersCtx;
    if (isNewEntry) {
      this.clearReportContext();
      this.clearBoardContext();
      this.clearProfileContext();
      this.clearExperimentContext();
      this.clearFeatureFlagContext();
      this.clearEventsContext();
      this.clearAgentFlowContext();
    }

    this.usersCtx = ctx;
    if (isNewEntry) {
      this.updateComponents();
    }
  }

  clearUsersContext() {
    this.usersCtx = null;
  }

  hasUsersContext(): boolean {
    return !!this.usersCtx;
  }

  getUsersContextForLlm(): CopilotCurrentPageContext[`usersContext`] | undefined {
    return this.usersCtx ?? undefined;
  }

  registerSessionReplayHandler(handler: (update: SessionReplayFiltersUpdate) => void) {
    this.sessionReplayHandler = handler;
    if (this.pendingSessionReplayFiltersUpdate) {
      handler(this.pendingSessionReplayFiltersUpdate);
      this.pendingSessionReplayFiltersUpdate = null;
    }
  }

  unregisterSessionReplayHandler() {
    this.sessionReplayHandler = null;
  }

  applySessionReplayFiltersUpdate(update: SessionReplayFiltersUpdate): boolean {
    if (this.sessionReplayHandler) {
      this.sessionReplayHandler(update);
      return true;
    }
    this.pendingSessionReplayFiltersUpdate = {
      ...(this.pendingSessionReplayFiltersUpdate || {}),
      ...update,
    };
    return false;
  }

  registerReplayPlaybackHandler(handler: (req: ReplayPlaybackRequest) => void) {
    this.replayPlaybackHandler = handler;
  }

  unregisterReplayPlaybackHandler() {
    this.replayPlaybackHandler = null;
  }

  playReplayAtTimestamp(req: ReplayPlaybackRequest): boolean {
    if (this.replayPlaybackHandler) {
      this.replayPlaybackHandler(req);
      return true;
    }
    return false;
  }

  setReplayDisplayMetadata(metadataByReplayId: Map<string, ReplayDisplayMetadata>) {
    this.replayDisplayMetadata = metadataByReplayId;
  }

  getReplayDisplayMetadata(replayId: string): ReplayDisplayMetadata | undefined {
    return this.replayDisplayMetadata.get(replayId);
  }

  registerBoardEditedHandler(handler: (detail: BoardEditedDetail) => void | Promise<void>) {
    this.boardEditedHandler = handler;
  }

  unregisterBoardEditedHandler() {
    this.boardEditedHandler = null;
  }

  notifyBoardEdited(detail: BoardEditedDetail) {
    if (this.notifiedBoardEditedToolCallIds.has(detail.toolCallId)) {
      return;
    }
    if (!this.boardEditedHandler) {
      return;
    }
    this.notifiedBoardEditedToolCallIds.add(detail.toolCallId);
    void this.runBoardEditedHandler(detail);
  }

  private async runBoardEditedHandler(detail: BoardEditedDetail) {
    try {
      await this.boardEditedHandler?.(detail);
    } catch (err) {
      // Drop the dedupe entry on failure so a future re-mount/retry can succeed.
      // Without this, a single transient fetch failure leaves the user looking at
      // stale data with no recovery path.
      this.notifiedBoardEditedToolCallIds.delete(detail.toolCallId);
      // eslint-disable-next-line no-console
      console.error(`[copilot] boardEditedHandler failed`, err);
    }
  }

  registerBoardSavingHandler(handler: (detail: BoardSaveDetail) => void | Promise<void>) {
    this.boardSavingHandler = handler;
  }

  unregisterBoardSavingHandler() {
    this.boardSavingHandler = null;
  }

  notifyBoardSaving(detail: BoardSaveDetail) {
    if (this.notifiedBoardSavingToolCallIds.has(detail.toolCallId)) {
      return;
    }
    if (!this.boardSavingHandler) {
      return;
    }
    this.notifiedBoardSavingToolCallIds.add(detail.toolCallId);
    void this.runBoardSavingHandler(detail);
  }

  private async runBoardSavingHandler(detail: BoardSaveDetail) {
    try {
      await this.boardSavingHandler?.(detail);
    } catch (err) {
      this.notifiedBoardSavingToolCallIds.delete(detail.toolCallId);
      // eslint-disable-next-line no-console
      console.error(`[copilot] boardSavingHandler failed`, err);
    }
  }

  registerBoardSavedHandler(handler: (detail: BoardSaveDetail) => void | Promise<void>) {
    this.boardSavedHandler = handler;
  }

  unregisterBoardSavedHandler() {
    this.boardSavedHandler = null;
  }

  notifyBoardSaved(detail: BoardSaveDetail) {
    if (this.notifiedBoardSavedToolCallIds.has(detail.toolCallId)) {
      return;
    }
    if (!this.boardSavedHandler) {
      return;
    }
    this.notifiedBoardSavedToolCallIds.add(detail.toolCallId);
    void this.runBoardSavedHandler(detail);
  }

  private async runBoardSavedHandler(detail: BoardSaveDetail) {
    try {
      await this.boardSavedHandler?.(detail);
    } catch (err) {
      this.notifiedBoardSavedToolCallIds.delete(detail.toolCallId);
      // eslint-disable-next-line no-console
      console.error(`[copilot] boardSavedHandler failed`, err);
    }
  }

  registerEventMetadataFilledHandler(handler: (detail: EventMetadataFilledDetail) => void | Promise<void>) {
    this.eventMetadataFilledHandler = handler;
  }

  unregisterEventMetadataFilledHandler() {
    this.eventMetadataFilledHandler = null;
  }

  notifyEventMetadataFilled(detail: EventMetadataFilledDetail) {
    if (this.notifiedEventMetadataFilledToolCallIds.has(detail.toolCallId)) {
      return;
    }
    if (!this.eventMetadataFilledHandler) {
      return;
    }
    this.notifiedEventMetadataFilledToolCallIds.add(detail.toolCallId);
    void this.runEventMetadataFilledHandler(detail);
  }

  private async runEventMetadataFilledHandler(detail: EventMetadataFilledDetail) {
    try {
      await this.eventMetadataFilledHandler?.(detail);
    } catch (err) {
      this.notifiedEventMetadataFilledToolCallIds.delete(detail.toolCallId);
      // eslint-disable-next-line no-console
      console.error(`[copilot] eventMetadataFilledHandler failed`, err);
    }
  }

  setSessionReplayContext(ctx: CopilotCurrentPageContext[`sessionReplayContext`]) {
    this.sessionReplayCtx = ctx;
  }

  clearSessionReplayContext() {
    this.sessionReplayCtx = null;
  }

  setActiveReplayIds(replayIds: string[]) {
    if (!this.sessionReplayCtx) {
      return;
    }
    this.sessionReplayCtx = {
      ...this.sessionReplayCtx,
      activeReplayIds: replayIds.length ? replayIds : undefined,
    };
  }

  // Presence, not size: the SR page owns the lifecycle, so a set context means the user
  // is on the SR page even when zero replays match their filters.
  hasSessionReplayContext(): boolean {
    return !!this.sessionReplayCtx;
  }

  getSessionReplayContextForLlm(): CopilotCurrentPageContext[`sessionReplayContext`] | undefined {
    if (!this.sessionReplayCtx) {
      return undefined;
    }
    // The LLM serializer drops empty arrays, so an empty `replayIds` would take the whole
    // context down with it; `replayCount` survives and is what says "zero replays match".
    return {...this.sessionReplayCtx, replayCount: this.sessionReplayCtx.replayIds.length};
  }

  private toLlmReportRequestContext(params?: BookmarkParamsUnionType): UnknownObj | undefined {
    if (!params) {
      return undefined;
    }

    if (`sections` in params) {
      const bookmark = params as InsightsBookmarkParams;
      return pick(bookmark, LLM_INSIGHTS_REQUEST_FIELDS);
    }

    // Destructure to exclude visual/display properties that aren't useful for LLM analysis
    const {displayOptions, columnWidths, legend, sorting, ...semanticParams} = params as any;
    return {...semanticParams};
  }

  private toLlmReportResponseContext(response?: BaseQueryResultsType | BaseQueryErrorType): UnknownObj | undefined {
    if (!response) {
      return undefined;
    }

    // Errors are useful as-is for LLM analysis
    if (`error` in response) {
      const {error, message, status, data} = response as BaseQueryErrorType;
      return {error, message, status, data};
    }

    // For successful results, keep all data arrays and semantic fields
    // Exclude only metadata that's not useful for LLM analysis
    const {computed_at: _computedAt, meta, ...relevantData} = response as any;

    // Extract only useful metadata if present
    const filteredMeta = meta?.report_sections ? {report_sections: meta.report_sections} : undefined;

    return {
      ...relevantData,
      ...(filteredMeta ? {meta: filteredMeta} : {}),
    };
  }

  subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
    return this;
  }

  bindToComponent(component: Component<any>) {
    this.connectedComponents.add(component);
  }

  unbindFromComponent(component: Component<any>) {
    this.connectedComponents.delete(component);
  }

  private updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }

    for (const subscription of this.subscriptions) {
      subscription();
    }
  }

  generateContextBody() {
    const body: {currentPageContext: CopilotCurrentPageContext | undefined} = {
      currentPageContext: undefined,
    };

    if (this.boardContext) {
      // Chat receives dashboard metadata only; cards are fetched on demand
      // via the getBoardDetails tool. Playlist cards are the exception: their
      // replay lists are resolved client-side when the card loads (the engine
      // cannot compute them), so they ride along inline.
      const playlists = Object.values(this.boardContext.cards)
        .filter((card) => card.playlist)
        .map((card) => ({
          title: card.title,
          description: card.description,
          name: card.playlist!.name,
          replayIds: card.playlist!.replayIds,
          totalReplayCount: card.playlist!.totalReplayCount,
          truncated: card.playlist!.truncated,
        }));
      body.currentPageContext = {
        boardContext: {
          dashboardId: this.boardContext.dashboardId?.toString(),
          title: this.boardContext.title,
          description: this.boardContext.description,
          canEdit: this.boardContext.canEdit,
          dateRange: this.boardContext.dateRange,
          // The bookmark-section types allow `null`, but the wire type is
          // `UnknownObj[] | undefined`. Widen at the boundary — the payload
          // is transported as plain JSON so the loose shape is safe.
          filters: this.boardContext.filters as unknown as UnknownObj[] | undefined,
          groupClauses: this.boardContext.groupClauses as unknown as UnknownObj[] | undefined,
          cohortClauses: this.boardContext.cohortClauses as unknown as UnknownObj[] | undefined,
          // The bookmark IDs of every card on this board, so queryBookmark can
          // gate the board-level context (dateRange/filters/breakdowns) on the
          // bookmark actually belonging to this board — an off-board bookmark
          // (the LLM followed getBoardDetails to a different board) must not
          // inherit this board's context.
          bookmarkIds: Object.values(this.boardContext.cards)
            .map((card) => card.bookmarkId)
            .filter((id): id is number => typeof id === `number`),
          playlists: playlists.length ? playlists : undefined,
          // Only attach RCA metadata that belongs to the board being viewed —
          // rcaCtx can lag behind on fast board-to-board navigation.
          rcaContext:
            this.rcaCtx && this.rcaCtx.dashboardId.toString() === this.boardContext.dashboardId?.toString()
              ? this.rcaCtx
              : undefined,
        },
      };
    } else if (this.hasReportContext()) {
      body.currentPageContext = {
        reportContext: this.getReportContextForLlm(),
      };
    } else if (this.hasAgentIntelligenceContext()) {
      // Before homeContext on purpose: home context persists until the next
      // clearChat (the home hero sets it imperatively), whereas this one is
      // cleared when its page unmounts, so when present it is the live page.
      body.currentPageContext = {
        agentIntelligenceContext: this.getAgentIntelligenceContextForLlm(),
      };
    } else if (this.hasHomeContext()) {
      body.currentPageContext = {
        homeContext: this.getHomeContextForLlm(),
      };
    } else if (this.hasProfileContext()) {
      body.currentPageContext = {
        profileContext: this.getProfileContextForLlm(),
      };
    } else if (this.hasExperimentContext()) {
      body.currentPageContext = {
        experimentContext: this.getExperimentContextForLlm(),
      };
    } else if (this.hasFeatureFlagContext()) {
      body.currentPageContext = {
        featureFlagContext: this.getFeatureFlagContextForLlm(),
      };
    } else if (this.hasAgentFlowContext()) {
      body.currentPageContext = {
        agentFlowContext: this.getAgentFlowContextForLlm(),
      };
    } else if (this.hasSessionReplayContext()) {
      body.currentPageContext = {
        sessionReplayContext: this.getSessionReplayContextForLlm(),
      };
    } else if (this.hasEventsContext()) {
      body.currentPageContext = {
        eventsContext: this.getEventsContextForLlm(),
      };
    } else if (this.hasUsersContext()) {
      body.currentPageContext = {
        usersContext: this.getUsersContextForLlm(),
      };
    }

    return body;
  }
}
